import type { WorkflowStatus } from '@/api/workflows';

/**
 * Status used by the dashboard card badge. Extends the raw BE
 * {@link WorkflowStatus} with derived states the Originator's tabs / version
 * history split out: 'PendingPublishing' = Approved + !isActive (awaiting
 * Approver to flip Active on); 'Published' = Approved + isActive (live for
 * advisors); 'Unpublished' = a VERSION-scoped state — this version is still
 * `template.currentVersionNumber` but the Approver took the whole workflow
 * offline, distinct from a version truly superseded by a newer one
 * ('Archived', which has no DisplayStatus entry — it's rendered directly by
 * StatusTag).
 */
export type DisplayStatus = WorkflowStatus | 'PendingPublishing' | 'Published' | 'Unpublished';

export const statusColor = (status: DisplayStatus): string => {
  switch (status) {
    case 'Draft':
      return 'blue';
    case 'Pending':
      return 'gold';
    case 'Approved':
      return 'green';
    case 'PendingPublishing':
      return 'cyan';
    case 'Published':
      return 'green';
    case 'Unpublished':
      return 'orange';
    case 'Rejected':
      return 'red';
  }
};

/** Normalize an unknown error from axios into the structured envelope shape we use server-side. */
export type ApiErrorDetails = {
  code?: string;
  message?: string;
  details?: Array<{ field?: string; message: string }>;
  /** HTTP status code when known — lets callers branch on 409 (lock conflict) vs other 4xx. */
  status?: number;
  /** Raw response body so callers needing structured fields beyond the standard envelope can read them. */
  body?: unknown;
};

export function extractApiError(err: unknown): ApiErrorDetails {
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof (err as { response: unknown }).response === 'object'
  ) {
    const response = (err as { response: { data?: unknown; status?: number } }).response;
    const status = typeof response?.status === 'number' ? response.status : undefined;
    if (response?.data && typeof response.data === 'object') {
      return { ...(response.data as ApiErrorDetails), status, body: response.data };
    }
    return { message: err instanceof Error ? err.message : 'Unknown error', status };
  }
  return { message: err instanceof Error ? err.message : 'Unknown error' };
}
